import { Box } from '@mui/material';
import React, { CSSProperties, FC, useMemo } from 'react';
import { animated, Transition } from '@react-spring/web';
import {
  AnimateCounterProps,
  AnimatedItem,
  DEFAULT_FONT_SIZE,
  separators,
  springConfig,
} from './types';

export const AnimatedStat: FC<AnimateCounterProps> = ({
  value,
  locale,
  prefix,
  suffix,
  formatOptions,
  addedCellStyle,
  fontSize = DEFAULT_FONT_SIZE,
}) => {
  const fontSizeValue = useMemo(() => fontSize, [fontSize]);
  const fontWidth = useMemo(() => fontSizeValue * 0.5, [fontSizeValue]);
  const valueStr = useMemo(
    () => (Number(value) || Number(0)).toLocaleString(locale, formatOptions),
    [value, locale, formatOptions],
  );
  const valueStrArray = useMemo(() => {
    const currValue = valueStr.split('');

    if (prefix) {
      currValue.unshift(prefix);
    }
    if (suffix) {
      currValue.push(suffix);
    }

    return currValue;
  }, [prefix, suffix, valueStr]);

  const { items, totalWidth } = useMemo(
    () =>
      valueStrArray.reduce(
        (acc, val, i) => {
          const precedingItem = acc.items[i - 1];
          const currentItem = {
            value: val,
            x: 0,
            y: fontWidth,
            key: `${i}-${val}`,
          };

          if (precedingItem) {
            currentItem.x = separators.includes(precedingItem.value)
              ? precedingItem.x + fontWidth * 0.475
              : precedingItem.x + fontWidth;
          }

          acc.items.push(currentItem);
          acc.totalWidth += currentItem.x - acc.totalWidth;

          return acc;
        },
        {
          items: [],
          totalWidth: 0,
        } as {
          items: { value: string; x: number; y: number; key: string }[];
          totalWidth: number;
        },
      ),
    [valueStrArray, fontWidth],
  );

  const wrapWidth = useMemo(
    () => totalWidth + fontWidth,
    [totalWidth, fontWidth],
  ); // width of container

  const cellStyle: CSSProperties = useMemo(
    () => ({ ...addedCellStyle, position: 'absolute', left: 0 }),
    [addedCellStyle],
  );

  return (
    <Box
      sx={{
        width: wrapWidth,
        position: 'relative',
        height: fontSizeValue,
        fontFamily: ({ typography }) => typography.fontFamily,
      }}
    >
      <Transition
        items={items}
        keys={(v: AnimatedItem) => v.key}
        initial={(item: AnimatedItem) => ({ ...item, y: item.y, opacity: 0 })}
        from={(item: AnimatedItem) => ({ ...item, y: -item.y, opacity: 0 })}
        enter={(item: AnimatedItem) => ({
          ...item,
          y: 0,
          x: item.x,
          opacity: 1,
        })}
        leave={(item: AnimatedItem) => ({
          ...item,
          y: item.y,
          x: item.x,
          opacity: 0,
        })}
        config={springConfig}
      >
        {(item: AnimatedItem, _) => (
          <animated.span
            style={{
              ...cellStyle,
              opacity: item.opacity,
              fontSize: fontSizeValue,
              left: item.x,
              top: item.y,
              transform: `translate3d(${item.x}px,${item.y}px,0px)`,
            }}
          >
            {item.value}
          </animated.span>
        )}
      </Transition>
    </Box>
  );
};
